import type { Product } from '../types';

/**
 * Catalog / search / listings sort contract.
 * API `filter` values stay stable for backend cache keys (latest | price-asc | price-desc | popular).
 */
export type ProductSortKey = 'newest' | 'priceAsc' | 'priceDesc' | 'popular';

export const DEFAULT_PRODUCT_SORT: ProductSortKey = 'newest';

export type ProductSortOption = {
    key: ProductSortKey;
    /** Value sent as query `filter` to catalog/search APIs */
    apiValue: string;
    labelKey: string;
    labelFallback: string;
};

export const PRODUCT_SORT_OPTIONS: readonly ProductSortOption[] = [
    {
        key: 'newest',
        apiValue: 'latest',
        labelKey: 'catalog.sort.newest',
        labelFallback: 'Newest first',
    },
    {
        key: 'priceAsc',
        apiValue: 'price-asc',
        labelKey: 'catalog.sort.priceAsc',
        labelFallback: 'Price: Low to High',
    },
    {
        key: 'priceDesc',
        apiValue: 'price-desc',
        labelKey: 'catalog.sort.priceDesc',
        labelFallback: 'Price: High to Low',
    },
    {
        key: 'popular',
        apiValue: 'popular',
        labelKey: 'catalog.sort.popular',
        labelFallback: 'Most Popular',
    },
] as const;

export function productSortApiValue(key: ProductSortKey): string {
    return PRODUCT_SORT_OPTIONS.find((o) => o.key === key)?.apiValue ?? 'latest';
}

export function productSortKeyFromApi(apiValue: string | null | undefined): ProductSortKey {
    const v = (apiValue ?? '').trim().toLowerCase();
    const match = PRODUCT_SORT_OPTIONS.find((o) => o.apiValue === v);
    return match?.key ?? DEFAULT_PRODUCT_SORT;
}

export function productSortKeyFromParam(param: string | null | undefined): ProductSortKey {
    if (!param) return DEFAULT_PRODUCT_SORT;
    const allowed = PRODUCT_SORT_OPTIONS.map((o) => o.key);
    return (allowed as string[]).includes(param) ? (param as ProductSortKey) : DEFAULT_PRODUCT_SORT;
}

/**
 * Client-side sort for full in-memory lists (my listings, My Rebaby recs).
 * Mirrors server ApplyProductSort for consistent UX.
 */
export function sortProductsClient(products: Product[], key: ProductSortKey): Product[] {
    const copy = [...products];
    const created = (p: Product) => (p.createdAt ? Date.parse(p.createdAt) : 0);
    const idDesc = (a: Product, b: Product) => (b.id || 0) - (a.id || 0);

    switch (key) {
        case 'priceAsc':
            return copy.sort((a, b) => a.price - b.price || created(b) - created(a) || idDesc(a, b));
        case 'priceDesc':
            return copy.sort((a, b) => b.price - a.price || created(b) - created(a) || idDesc(a, b));
        case 'popular':
            // Server uses ViewCount → product AverageRating → CreatedAt → Id.
            // Public Product DTO exposes viewCount (+ favouriteCount on my-products); no product rating field.
            return copy.sort(
                (a, b) =>
                    (b.viewCount || 0) - (a.viewCount || 0) ||
                    (b.favouriteCount || 0) - (a.favouriteCount || 0) ||
                    created(b) - created(a) ||
                    idDesc(a, b)
            );
        case 'newest':
        default:
            return copy.sort((a, b) => created(b) - created(a) || idDesc(a, b));
    }
}
